import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SchemaJsonLd from "@/components/base/SchemaJsonLd";
import { siteInfo } from "@/mocks/news";
import Principles from "@/pages/about/components/Principles";
import Testimonials from "@/pages/about/components/Testimonials";
import FeedbackForm from "@/components/feature/FeedbackForm";

const stats = [
  { value: "6", label: "内容栏目", icon: "ri-layout-grid-line" },
  { value: "60+", label: "每日更新条目", icon: "ri-refresh-line" },
  { value: "07-23", label: "持续更新时段", icon: "ri-time-line" },
];

const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      name: "关于 51cg网",
      description: "51cg网的平台定位、内容原则、读者反馈与联系方式。",
      inLanguage: "zh-CN",
    },
    {
      "@type": "ContactPage",
      name: "联系 51cg网",
      description: "通过邮箱或反馈表单与 51cg网 取得联系。",
      inLanguage: "zh-CN",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://ryvucy.cn/" },
        { "@type": "ListItem", position: 2, name: "关于我们" },
      ],
    },
  ],
};

export default function AboutPage() {
  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Header />
      <main className="flex-1">
        <SchemaJsonLd data={schema} />

        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
            <Breadcrumb items={[{ label: "关于我们" }]} />
            <span className="mt-5 block text-xs font-medium tracking-widest text-primary-500">
              ABOUT {siteInfo.name}
            </span>
            <h1 className="mt-3 max-w-3xl font-serif-cn text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
              关于 51cg网
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground-600">
              {siteInfo.intro}
            </p>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {stats.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <div>
                    <p className="font-serif-cn text-2xl font-bold text-foreground-950">
                      {item.value}
                    </p>
                    <p className="text-xs text-foreground-500">{item.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 space-y-14 md:mt-20 md:space-y-20">
          <section aria-label="平台定位" className="mx-auto max-w-[1400px] px-4 md:px-6">
            <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-14">
              <div className="h-64 w-full overflow-hidden rounded-lg md:h-[420px]">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20editorial%20newsroom%20desk%20with%20notebooks%20and%20coffee%2C%20soft%20morning%20light%2C%20clean%20minimal%20documentary%20photography%2C%20neutral%20tones&width=1200&height=900&seq=about-newsroom-01&orientation=landscape"
                  alt="51cg网 编辑工作场景示意"
                  title="51cg网 平台定位 资讯编辑"
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div>
                <span className="text-xs font-medium tracking-widest text-primary-500">
                  平台定位
                </span>
                <h2 className="mt-3 font-serif-cn text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
                  做一个够快、也够清楚的资讯入口
                </h2>
                <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-600">
                  <p>
                    热点每天都在变，但大多数人真正需要的不是更多的信息，而是把正在发生的事快速理清楚。
                    51cg网把"最新爆料、即时快讯、今日速递、突发消息"集中到一条信息流里，减少来回切换的麻烦。
                  </p>
                  <p>
                    我们既保留即时快讯的短平快，也用深度爆料与热点追踪补上背景，
                    让追热点的过程不至于碎片化。所有内容按栏目归类，读者可以只看自己关心的方向。
                  </p>
                  <p>
                    平台面向的是普通的资讯读者，而不是专业研究者，因此表达尽量直白，
                    少用行话，把"发生了什么、进展到哪、接下来看什么"讲明白。
                  </p>
                </div>
              </div>
            </div>
          </section>

          <Principles />
          <Testimonials />

          <section aria-label="联系我们" className="border-t border-background-200 bg-background-100">
            <div className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-16">
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-16">
                <div>
                  <span className="text-xs font-medium tracking-widest text-primary-500">
                    联系我们
                  </span>
                  <h2 className="mt-3 font-serif-cn text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
                    发现内容有问题？直接告诉我们
                  </h2>
                  <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                    如果你在站内看到与事实不符的内容，或者有值得关注的线索，
                    都可以通过下面的方式联系我们。核实后我们会尽快更正或跟进。
                  </p>
                  <ul className="mt-6 space-y-3 text-sm text-foreground-700">
                    <li className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 text-primary-600">
                        <i className="ri-mail-line" />
                      </span>
                      <a href={`mailto:${siteInfo.email}`} className="transition hover:text-primary-600">
                        {siteInfo.email}
                      </a>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 text-primary-600">
                        <i className="ri-weibo-fill" />
                      </span>
                      <span>{siteInfo.weibo}</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 text-primary-600">
                        <i className="ri-time-line" />
                      </span>
                      <span>每日 07:00 - 23:30 在线处理反馈</span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
                  <FeedbackForm />
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}